import type { ApiError } from '@approvalmax/types';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { stateTree } from 'modules/data';
import { helpers as pageHelpers, hooks as pageHooks } from 'modules/page';
import { useSelector } from 'modules/react-redux';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { hasChangesState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { pageConfig } from '../../config';
import { discardPageState, loadPageData, loadTemplates, showWorkflowPage } from '../actions';
import { getHasChanges } from '../selectors/templateSelectors';
import { messages } from './messages';

export const useWorkflowPageData = () => {
    const dispatch = useDispatch();
    const { companyId, workflowId } = useParams<{ companyId: string; workflowId: string }>();
    const { company } = pageHooks.useNonExistentCompanyError(companyId);
    const hasChanges = useSelector(getHasChanges);
    const setHasChanges = useSetAtom(hasChangesState);

    useEffect(() => {
        setHasChanges(hasChanges);
    }, [hasChanges, setHasChanges]);

    useEffect(() => {
        return () => {
            dispatch(discardPageState());
        };
    }, [dispatch]);

    const { isInitialLoading, error } = useQuery<stateTree.Entities, ApiError>(
        ['workflow', companyId, workflowId],
        async () => await loadTemplates({ companyId }),
        {
            enabled: !!company,
            onSuccess: (data) => {
                if (data.templates[workflowId]) {
                    dispatch(loadPageData(pageConfig.innerActivePageId, companyId, data));
                    dispatch(showWorkflowPage(companyId, workflowId));
                } else {
                    pageHelpers.redirectAndNotify({
                        pathToRedirect: getPath(Path.companyWorkflows, companyId),
                        errorMessage: messages.templateNotFound,
                    });
                }
            },
        }
    );

    pageHooks.usePageError(error);

    return { isInitialLoading };
};
